import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges, OnChanges, ViewChild } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { ToastService, ToastType } from '../../service/toast/toast.service';

@Component({
  selector: 'app-user-config-modal',
  templateUrl: './user-config-modal.component.html',
  styleUrls: ['./user-config-modal.component.scss'],
  standalone: false
})
export class UserConfigModalComponent implements OnInit, OnChanges {
  @ViewChild('userConfigModal') userConfigModal: DialogModule;
  @Input() isVisible: boolean = false;
  @Input() modalTitle: string = 'User Configurations';
  @Input() modalType: string = 'dropdown';
  @Input() dropdownOptions: any[] = []; // Full 6 KPI options with { id, label }
  @Input() userKpiData: any[] = [];     // 4 user KPIs with kpiType & defaultOrder

  @Output() onSave = new EventEmitter<Object>();
  @Output() onReset = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  selectedKpis: (number | null)[] = [null, null, null, null];
  filteredDropdownOptions: { label: string, value: number, disabled: boolean }[][] = [];

  constructor(private toastService: ToastService) {}

  ngOnInit() {
    this.initializeSelectedKpisFromInput();
    this.updateAllDropdownOptions();
  }

  initializeSelectedKpisFromInput(): void {
    const orderedKpis: (number | null)[] = [null, null, null, null];

    this.userKpiData.forEach(kpi => {
      const order = kpi.defaultOrder;
      if (order >= 1 && order <= 4) {
        orderedKpis[order - 1] = kpi.kpiType; // Use kpiType (which is id)
      }
    });

    this.selectedKpis = orderedKpis;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes in dropdownOptions and userKpiData
    if (changes['dropdownOptions'] || changes['userKpiData']) {
      this.initializeSelectedKpisFromInput();
      this.updateAllDropdownOptions(); // Update filtered options when either dropdownOptions or userKpiData change
    }
  }

  updateAllDropdownOptions(): void {
    this.filteredDropdownOptions = []; // Reset dropdown options
    for (let i = 0; i < 4; i++) {
      this.filteredDropdownOptions[i] = this.getFilteredOptions(i);
    }
  }

  getFilteredOptions(index: number): { label: string, value: number, disabled: boolean }[] {
    return this.dropdownOptions.map(option => ({
      label: option.label,
      value: option.value,
      disabled: this.selectedKpis.includes(option.id) && this.selectedKpis[index] !== option.id
    }));
  }

  onDropdownChange(index: number, value: number): void {
    this.selectedKpis[index] = value;
    this.updateAllDropdownOptions();
  }

  resetToDefault(): void {
    const orderedKpis: (number | null)[] = [null, null, null, null];

    // Assuming dropdownOptions are sorted in the default order (defaultOrder)
    this.dropdownOptions.forEach(option => {
      const order = option.defaultOrder; // Make sure dropdownOptions have defaultOrder
      if (order >= 1 && order <= 4) {
        orderedKpis[order - 1] = option.value; // Set the value in the right position based on defaultOrder
      }
    });

    this.selectedKpis = orderedKpis;
    this.updateAllDropdownOptions();
    this.emitReset();

    this.toastService.showToast(ToastType.SUCCESS, "KPI widgets have been reset to default.", 5000);
  }

  emitSave(): void {
    // Create the preferences array from selectedKpis with the correct structure for API request
    const preferences = this.selectedKpis.map((kpiId, index) => {
      // Check if the kpiId is not null, and create an object for each
      if (kpiId !== null) {
        return {
          kpiId: kpiId,  // The kpiId is the value of selectedKpis (i.e., 1, 2, 3, 4, etc.)
          widgetOrder: index + 1  // widgetOrder is the index (1-based)
        };
      }
      return null;
    }).filter(pref => pref !== null); // Remove any null values if the user hasn't selected something

    // Emit the preferences in the required format
    this.onSave.emit({ preferences });
  }

  emitReset(): void {
    this.onReset.emit();
  }

  onDialogClose(): void {
    this.emitClose();
  }

  emitClose(): void {
    this.onClose.emit();
  }

  isSaveDisabled(): boolean {
    return this.selectedKpis.some(value => value === null);
  }
}

